/**
 * DiagramGallery — grouped preview gallery for diagram-spec JSON metadata.
 *
 * Replaces the old standalone HTML gallery generation path with a reusable
 * React component in the presentation layer.
 *
 * Each kind's grid attaches the shared `useAutoAnimate` ref so that when
 * a consumer filters or re-sorts the gallery, item enter / exit / reorder
 * runs through a FLIP transition automatically. `useAutoAnimate` honors
 * `prefers-reduced-motion` upstream, so no extra branch is required.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useMemo } from "react";
import {
  diagramKindHeading,
  diagramKindRank,
  type DiagramGalleryItem,
} from "../diagrams/diagramSupport.js";
import { GRAY, PRIMARY } from "../design-system/tokens.js";
import { useAutoAnimate } from "../hooks/useAutoAnimate.js";
import { Card } from "./Card.js";
import { EmptyState } from "./EmptyState.js";
import { MarkdownPanel } from "./MarkdownPanel.js";
import { Pill } from "./Pill.js";
import { Section } from "./Section.js";
export { createDiagramGalleryItems } from "../diagrams/diagramSupport.js";
export type { DiagramGalleryItem, DiagramSpecMeta } from "../diagrams/diagramSupport.js";

export interface DiagramGalleryProps {
  /** Prepared gallery items, typically created from diagram-spec JSON metadata. */
  readonly items: readonly DiagramGalleryItem[];
  /** Optional page title. */
  readonly title?: string;
  /** Optional supporting description. */
  readonly description?: string;
}

const GRID_STYLE: React.CSSProperties = {
  display: "grid",
  gap: "1rem",
  gridTemplateColumns: "repeat(auto-fit, minmax(20rem, 1fr))",
};

interface DiagramGroupProps {
  readonly kind: string;
  readonly members: readonly DiagramGalleryItem[];
}

interface DiagramGalleryGroup {
  readonly kind: string;
  readonly members: readonly DiagramGalleryItem[];
}

/**
 * One kind-grouped grid of gallery cards. The grid div carries the
 * `useAutoAnimate` ref so item changes within the group animate with
 * FLIP transitions; extracting it as its own component is what makes
 * the rules-of-hooks-clean call to `useAutoAnimate` legal (the parent
 * iterates over groups).
 */
const DiagramGroup = React.memo(function DiagramGroup({
  kind,
  members,
}: DiagramGroupProps): React.JSX.Element {
  const [gridRef] = useAutoAnimate<HTMLDivElement>();
  return (
    <Section
      title={`${diagramKindHeading(kind)} (${members.length})`}
      description={`Preview assets derived from ${kind}.`}
    >
      <div ref={gridRef} style={GRID_STYLE} data-testid="diagram-gallery-grid">
        {members.map((item) => (
          <Card key={item.id}>
            <div style={{ display: "grid", gap: "0.75rem" }}>
              <div>
                <div style={{ fontSize: "1rem", fontWeight: 700 }}>{item.title}</div>
                <div style={{ color: GRAY[500], fontSize: "0.75rem", marginTop: "0.25rem" }}>
                  <code>{item.id}</code>
                </div>
              </div>

              {item.description !== undefined && <MarkdownPanel>{item.description}</MarkdownPanel>}

              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
                <Pill tone="info">{item.kind}</Pill>
                {item.theorems.map((theorem) => (
                  <Pill key={`${item.id}-${theorem}`} tone="neutral" mono>
                    {theorem}
                  </Pill>
                ))}
              </div>

              <div
                style={{
                  backgroundColor: GRAY[100],
                  border: `1px solid ${GRAY[300]}`,
                  borderRadius: "0.5rem",
                  minHeight: "15rem",
                  overflow: "hidden",
                }}
              >
                {item.previewKind === "html" ? (
                  <iframe
                    loading="lazy"
                    sandbox="allow-same-origin"
                    src={item.previewSource}
                    title={item.title}
                    style={{
                      backgroundColor: "white",
                      border: "none",
                      display: "block",
                      minHeight: "15rem",
                      width: "100%",
                    }}
                  />
                ) : (
                  <img
                    alt={item.title}
                    loading="lazy"
                    src={item.previewSource}
                    style={{
                      display: "block",
                      height: "100%",
                      maxWidth: "100%",
                      objectFit: "contain",
                      width: "100%",
                    }}
                  />
                )}
              </div>

              <div style={{ color: GRAY[500], fontSize: "0.75rem" }}>
                Preview source: <code style={{ color: PRIMARY[700] }}>{item.previewSource}</code>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </Section>
  );
});

/** Render a grouped gallery of generated diagram previews. */
export const DiagramGallery = React.memo(function DiagramGallery({
  items,
  title = "Diagram gallery",
  description = "Registry-backed previews of rendered diagram assets grouped by renderer kind.",
}: DiagramGalleryProps): React.JSX.Element {
  const groups = useMemo<readonly DiagramGalleryGroup[]>(() => {
    const byKind = new Map<string, DiagramGalleryItem[]>();
    for (const item of [...items].sort(
      (left, right) =>
        diagramKindRank(left.kind) - diagramKindRank(right.kind) ||
        left.title.localeCompare(right.title),
    )) {
      const current = byKind.get(item.kind);
      if (current === undefined) byKind.set(item.kind, [item]);
      else current.push(item);
    }
    return Array.from(byKind, ([kind, members]) => ({ kind, members }));
  }, [items]);

  return (
    <div
      style={{
        color: GRAY[900],
        display: "grid",
        gap: "1.5rem",
        fontFamily: "Inter, system-ui, sans-serif",
      }}
    >
      <header>
        <h1 style={{ margin: 0, fontSize: "2rem", lineHeight: 1.15 }}>{title}</h1>
        <MarkdownPanel style={{ color: GRAY[600], marginTop: "0.5rem" }}>
          {description}
        </MarkdownPanel>
      </header>

      {items.length === 0 ? (
        <EmptyState
          icon="🖼️"
          title="No rendered diagrams are available"
          description="Add diagram metadata or generated assets, then rebuild the gallery."
        />
      ) : (
        groups.map(({ kind, members }) => <DiagramGroup key={kind} kind={kind} members={members} />)
      )}
    </div>
  );
});
